import { lazy, Suspense, useEffect, useRef, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, ArrowRight, ArrowUpRight, Phone, MessageCircle, Menu, Mail, Instagram } from "lucide-react";
import { CATALOG, PHONE_DISPLAY, PHONE_TEL, EMAIL, INSTAGRAM, waLink } from "@/data/site";
import { Logo } from "@/components/Logo";
const MobileNav = lazy(() => import("@/components/layout/MobileNav").then((m) => ({ default: m.MobileNav })));

const MENUS = Array.isArray(CATALOG?.menus) ? CATALOG.menus : [];

function MegaPanel({ menu, onNavigate, alignRight }) {
  return (
    <div
      id={`mega-${menu.key}`}
      role="region"
      aria-label={`${menu.label} menu`}
      data-lenis-prevent
      className={`mega-in absolute top-full z-50 mt-2 max-h-[calc(100vh-130px)] overflow-y-auto rounded-2xl border border-line bg-white p-6 shadow-[0_30px_80px_-30px_rgba(11,31,58,0.35)] ${alignRight ? "right-0 origin-top-right" : "-left-3 origin-top-left"} ${menu.columns.length > 2 ? "w-[min(860px,94vw)]" : "w-[min(620px,92vw)]"}`}
      data-testid={`mega-menu-panel-${menu.key}`}
    >
      <div className={`grid gap-x-8 ${menu.columns.length > 2 ? "grid-cols-3" : "grid-cols-2"}`}>
        {menu.columns.map((col, ci) => (
          <ul key={ci} className="space-y-0.5">
            {col.map((it) => (
              <li key={it.path}>
                <Link
                  to={it.path}
                  onClick={onNavigate}
                  className="group flex items-center justify-between rounded-lg px-3 py-2 text-[15px] font-medium text-ink transition-colors duration-200 hover:bg-gold-soft hover:text-gold-deep focus-visible:bg-gold-soft"
                  data-testid={`mega-link-${menu.key}-${it.path.replace(/\//g, "-").replace(/^-/, "")}`}
                >
                  <span>{it.label}</span>
                  <ArrowRight className="h-3.5 w-3.5 -translate-x-1 text-gold-deep opacity-0 transition-[opacity,transform] duration-200 group-hover:translate-x-0 group-hover:opacity-100" aria-hidden />
                </Link>
              </li>
            ))}
          </ul>
        ))}
      </div>
      <div className="mt-5 border-t border-line pt-4">
        <Link to={menu.cta.path} onClick={onNavigate} className="group inline-flex items-center gap-2 px-3 py-1.5 text-[15px] font-bold text-gold-deep transition-colors hover:text-saffron" data-testid={`mega-cta-${menu.key}`}>
          {menu.cta.label}
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden />
        </Link>
      </div>
    </div>
  );
}

function DesktopNav() {
  const [open, setOpen] = useState(null);
  const ref = useRef(null);
  const timer = useRef(null);
  const close = useCallback(() => setOpen(null), []);

  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(null); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(null); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, []);

  const hoverOpen = (k) => { clearTimeout(timer.current); timer.current = setTimeout(() => setOpen(k), 90); };
  const hoverClose = () => { clearTimeout(timer.current); timer.current = setTimeout(() => setOpen(null), 180); };

  return (
    <nav ref={ref} aria-label="Main" className="hidden xl:block" onMouseLeave={hoverClose}>
      <ul className="flex items-center gap-0.5">
        {MENUS.map((m, mi) => (
          <li key={m.key} className="relative" onMouseEnter={() => hoverOpen(m.key)}>
            <button
              type="button"
              aria-expanded={open === m.key}
              aria-controls={`mega-${m.key}`}
              onClick={() => setOpen((o) => (o === m.key ? null : m.key))}
              className={`flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-2 text-[14px] font-semibold transition-colors duration-200 ${open === m.key ? "text-gold-deep" : "text-ink hover:text-gold-deep"}`}
              data-testid={`mega-menu-trigger-${m.key}`}
            >
              {m.label}
              <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${open === m.key ? "rotate-180" : ""}`} aria-hidden />
            </button>
            {open === m.key && <MegaPanel menu={m} onNavigate={close} alignRight={mi >= 4} />}
          </li>
        ))}
      </ul>
    </nav>
  );
}

export function Header() {
  const [mobile, setMobile] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  useEffect(() => { setMobile(false); }, [loc.pathname]);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 20);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      <div className="hidden bg-ink text-[12.5px] text-zinc-300 md:block">
        <div className="container-x flex h-9 items-center justify-between">
          <p>Taxi booking across all of Rajasthan · Offices in Jodhpur, Jaisalmer, Udaipur &amp; Jaipur</p>
          <div className="flex items-center gap-5">
            <a href={`tel:${PHONE_TEL}`} className="flex items-center gap-1.5 hover:text-gold" data-testid="topbar-phone"><Phone className="h-3.5 w-3.5" aria-hidden />{PHONE_DISPLAY}</a>
            <a href={`mailto:${EMAIL}`} className="flex items-center gap-1.5 hover:text-gold" data-testid="topbar-email"><Mail className="h-3.5 w-3.5" aria-hidden />{EMAIL}</a>
            <a href={INSTAGRAM} target="_blank" rel="noopener noreferrer" aria-label="Lucky Taxi Service on Instagram" className="hover:text-gold" data-testid="topbar-instagram"><Instagram className="h-4 w-4" aria-hidden /></a>
          </div>
        </div>
      </div>
      <div className={`border-b transition-[background-color,box-shadow] duration-300 ${scrolled ? "border-line bg-white/95 shadow-[0_10px_30px_-20px_rgba(17,22,31,0.3)] backdrop-blur-xl" : "border-transparent bg-sand/95 backdrop-blur"}`}>
        <div className="relative mx-auto flex h-[78px] w-full max-w-[1440px] items-center justify-between gap-3 px-4 sm:px-6">
          <Link to="/" aria-label="Lucky Taxi Service home" data-testid="header-logo-link"><Logo /></Link>
          <DesktopNav />
          <div className="flex items-center gap-2">
            <a href={`tel:${PHONE_TEL}`} aria-label="Call Now" className="btn-outline hidden whitespace-nowrap !px-4 !py-2.5 sm:inline-flex" data-testid="nav-call-now-button"><Phone className="h-4 w-4" aria-hidden /><span className="xl:hidden min-[1400px]:inline">Call Now</span></a>
            <a href={waLink()} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="btn-wa hidden whitespace-nowrap !px-3.5 !py-2.5 lg:inline-flex" data-testid="nav-whatsapp-button"><MessageCircle className="h-4 w-4" aria-hidden /><span className="xl:hidden min-[1400px]:inline">WhatsApp</span></a>
            <Link to="/booking" className="btn-primary hidden whitespace-nowrap !px-4 !py-2.5 sm:inline-flex" data-testid="nav-book-now-button">Book Now<ArrowUpRight className="h-4 w-4" aria-hidden /></Link>
            <button type="button" onClick={() => setMobile(true)} className="grid h-11 w-11 place-items-center rounded-full border border-line bg-white xl:hidden" aria-label="Open menu" data-testid="mobile-menu-open-button">
              <Menu className="h-5 w-5" aria-hidden />
            </button>
          </div>
        </div>
      </div>
      {mobile && <Suspense fallback={null}><MobileNav open={mobile} onOpenChange={setMobile} menus={MENUS} /></Suspense>}
    </header>
  );
}
